/**
 * @see VisibilityBasedInterval options.
 */
interface IVisibilityBasedIntervalOptions {
    /**
     * Whether to execute the last missed callback when the tab becomes visible again.
     * @default true
     */
    executeMissedCallbackOnVisible?: boolean;

    /**
     * Whether to execute the callback immediately upon initialization if the tab is visible.
     * @default false
     */
    executeOnInitialization?: boolean;
}

/**
 * A utility class that executes a callback at regular intervals, but only when the tab is visible.
 * It can execute the last missed callback when the tab becomes visible again.
 */
class VisibilityBasedInterval {
    private readonly delay: number;
    private readonly executeMissedCallbackOnVisible: boolean;
    private readonly callback: () => void;
    private intervalId: NodeJS.Timeout | null = null;
    private lastExecutionTime: number = Date.now();

    /**
     * Creates a new VisibilityBasedInterval instance.
     * @param callback The function to execute at each interval.
     * @param delay The interval delay in milliseconds.
     * @param options Optional settings for the interval behavior.
     */
    constructor(
        callback: () => void,
        delay: number,
        options: IVisibilityBasedIntervalOptions = {}
    ) {
        this.callback = callback;
        this.delay = delay;
        this.executeMissedCallbackOnVisible = options.executeMissedCallbackOnVisible ?? true;

        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

        document.addEventListener("visibilitychange", this.handleVisibilityChange);

        if (options.executeOnInitialization)
            this.executeCallback();

        this.initInterval();
    }

    /**
     * Clears the interval and removes the visibility listener.
     * Make sure to call this when the interval is no longer needed to avoid memory leaks!
     */
    public clear(): void {
        this.clearInterval();
        document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    }

    /**
     * Re-initializes the interval with the same callback and delay.
     */
    private initInterval(): void {
        this.clearInterval();
        this.intervalId = setInterval(() => this.executeCallback(), this.delay);
    }

    /**
     * Clears the current interval if it exists.
     */
    private clearInterval(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    /**
     * Re-enables the callback execution and checks if the last missed callback should be executed.
     */
    private resume(): void {
        if (!this.executeMissedCallbackOnVisible)
            return;

        const now = Date.now();
        const timeSinceLastExecution = now - this.lastExecutionTime;

        if (timeSinceLastExecution >= this.delay) {
            this.initInterval();
            this.executeCallback();
        }
    }

    /**
     * Executes the callback immediately if enabled.
     * Also updates the last execution time to the current time.
     */
    private executeCallback(): void {
        if (document.hidden) {
            if (this.executeMissedCallbackOnVisible) // no need to continue running the interval, it will be resumed when the tab becomes visible
                this.clearInterval();
            return;
        }

        this.callback();
        this.lastExecutionTime = Date.now();
    }

    /**
     * Handles visibility change events. Resumes the interval when the tab becomes visible again.
     */
    private handleVisibilityChange(): void {
        if (!document.hidden)
            this.resume();
    }
}

export { IVisibilityBasedIntervalOptions, VisibilityBasedInterval };
